import type { FunctionComponent } from 'react';
import { SectionHeaderType } from 'corgi-types';
import type { StringMap } from 'corgi-types';

import { MessageType } from 'common/enums/Alert';
import { AlertWithHtml } from './AlertWithHtml';
import CartSectionHeaderAlert from './CartSectionHeaderAlert';
import { CartSectionHeaderWrapper } from './CartSectionHeaderWrapper';
import type { SectionHeaderWrapperProps } from './CartSectionHeaderWrapper';
import MyProfileHeaderAlert from './MyProfileHeaderAlert';

interface AlertComponentProps {
  alertType?: MessageType;
  alertMessage?: string;
  alertTranslationProps?: StringMap<string | number>;
}

export interface HeaderOptions {
  heading?: string;
  subHeading?: string;
  alertType?: MessageType;
  alertMessage?: string;
  AlertComponent?: FunctionComponent<AlertComponentProps>;
  WrapperComponent?: FunctionComponent<SectionHeaderWrapperProps>;
}

export const HEADER_TYPE_OPTIONS = new Map<
  Omit<SectionHeaderType, SectionHeaderType.Default>,
  HeaderOptions
>([
  [
    SectionHeaderType.Cart,
    {
      heading: 'h24_your_cart',
      subHeading: 'h24_cart_items_subheading',
      AlertComponent: CartSectionHeaderAlert,
      WrapperComponent: CartSectionHeaderWrapper,
    },
  ],
  [SectionHeaderType.OrdersOverview, { heading: 'h24_my_orders' }],
  [SectionHeaderType.MyAccount, { heading: 'h24_welcome', subHeading: 'h24_your_customer_number' }],
  [
    SectionHeaderType.MyAccountThirdPartyConnected,
    {
      heading: 'h24_welcome',
      subHeading: 'h24_your_customer_number',
      alertType: MessageType.Success,
      alertMessage: 'h24_account_linked_success',
    },
  ],
  [
    SectionHeaderType.MyAccountHomeClubSubFailed,
    {
      heading: 'h24_welcome',
      subHeading: 'h24_your_customer_number',
      alertType: MessageType.Warning,
      alertMessage: 'h24_hc_my_account_failed_request_alert',
    },
  ],
  [
    SectionHeaderType.MyAccountHomeClubOptOutSuccess,
    {
      heading: 'h24_welcome',
      subHeading: 'h24_your_customer_number',
      alertType: MessageType.Success,
      alertMessage: 'h24_hc_my_account_hc_opt_out_success',
    },
  ],
  [
    SectionHeaderType.MyAccountReferralSuccess,
    {
      heading: 'h24_welcome',
      subHeading: 'h24_your_customer_number',
      alertType: MessageType.Success,
      alertMessage: 'h24_my_account_referral_success',
    },
  ],
  [
    SectionHeaderType.MyAccountReferralAccountExists,
    {
      heading: 'h24_welcome',
      subHeading: 'h24_your_customer_number',
      alertType: MessageType.Notice,
      alertMessage: 'h24_my_account_referral_account_exists',
      AlertComponent: AlertWithHtml,
    },
  ],
  [
    SectionHeaderType.MyProfile,
    {
      heading: 'h24_my_customer_profile',
      AlertComponent: MyProfileHeaderAlert,
    },
  ],
  [
    SectionHeaderType.MyProfileThirdPartyConnected,
    {
      heading: 'h24_my_customer_profile',
      alertType: MessageType.Success,
      alertMessage: 'h24_account_linked_success',
      AlertComponent: MyProfileHeaderAlert,
    },
  ],
  [
    SectionHeaderType.MyProfileThirdPartyFailed,
    {
      heading: 'h24_my_customer_profile',
      alertType: MessageType.Error,
      alertMessage: 'h24_something_went_wrong',
      AlertComponent: MyProfileHeaderAlert,
    },
  ],
  [
    SectionHeaderType.ReclamationCancellationsRequestList,
    { heading: 'h24_cancellation_request_list_header' },
  ],
  [SectionHeaderType.ReclamationCancellationsRecap, { heading: 'h24_cancellation_recap_header' }],
  [
    SectionHeaderType.ReclamationCancellationsSuccess,
    {
      heading: 'h24_cancellation_success_header',
      alertType: MessageType.Success,
      alertMessage: 'h24_submission_page_alert_message',
    },
  ],
  [SectionHeaderType.ReclamationReturnsRequestList, { heading: 'h24_returns_request_list_header' }],
  [SectionHeaderType.ReclamationReturnsRecap, { heading: 'h24_returns_recap_header' }],
  [
    SectionHeaderType.ReclamationReturnsSuccess,
    {
      heading: 'h24_returns_success_header',
      alertType: MessageType.Success,
      alertMessage: 'h24_submission_page_alert_message',
    },
  ],
  [
    SectionHeaderType.ReclamationReturnsMarketplaceSuccess,
    {
      heading: 'h24_returns_success_header',
      alertType: MessageType.Success,
      alertMessage: 'h24_reclamation_submission_marketplace_alert_message',
    },
  ],
  [SectionHeaderType.SearchResultsEmpty, { heading: 'h24_search_results_empty_heading' }],
  [
    SectionHeaderType.Wishlist,
    { heading: 'h24_your_wishlist', subHeading: 'h24_cart_items_subheading' },
  ],
  [
    SectionHeaderType.ExternalReclamationReturnsRequestList,
    { heading: 'h24_returns_request_list_header' },
  ],
  [SectionHeaderType.ExternalReclamationReturnsRecap, { heading: 'h24_returns_recap_header' }],
  [
    SectionHeaderType.ExternalReclamationReturnsSuccess,
    {
      heading: 'h24_returns_success_header',
      alertType: MessageType.Success,
      alertMessage: 'h24_external_submission_page_alert_message',
    },
  ],
]);
